/**
 * pricingSessionsSlice — single source of truth for the tenant-wide pricing
 * session catalog (shared by every space's pricing table).
 *
 * Hydrated from the SSR fetch via PricingSessionsInitializer; the table
 * dispatches add/update/delete with the server-returned session after a
 * successful API call (mirrors `venuesSlice`). Reducers keep the list sorted by
 * `sortOrder`.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PricingSessionCatalog } from "@/components/settings/venues/pricing/types";

export interface PricingSessionsState {
  sessions: PricingSessionCatalog[];
  loading: boolean;
}

const initialState: PricingSessionsState = {
  sessions: [],
  loading: true,
};

const bySortOrder = (a: PricingSessionCatalog, b: PricingSessionCatalog) =>
  a.sortOrder - b.sortOrder;

const pricingSessionsSlice = createSlice({
  name: "pricingSessions",
  initialState,
  reducers: {
    /** Replace the catalog — used to hydrate from the API on mount. */
    setPricingSessions(state, action: PayloadAction<PricingSessionCatalog[]>) {
      state.sessions = [...action.payload].sort(bySortOrder);
    },
    setPricingSessionsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    addPricingSession(state, action: PayloadAction<PricingSessionCatalog>) {
      state.sessions.push(action.payload);
      state.sessions.sort(bySortOrder);
    },
    updatePricingSession(state, action: PayloadAction<PricingSessionCatalog>) {
      const idx = state.sessions.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.sessions[idx] = action.payload;
      state.sessions.sort(bySortOrder);
    },
    deletePricingSession(state, action: PayloadAction<string>) {
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
    },
  },
});

export const {
  setPricingSessions,
  setPricingSessionsLoading,
  addPricingSession,
  updatePricingSession,
  deletePricingSession,
} = pricingSessionsSlice.actions;

export default pricingSessionsSlice.reducer;
